import { Params } from 'react-router-dom';
import {
  NavGroupConfig,
  NavGroupId,
  NavLinkConfig,
  NavLinkId,
  ProfileNavLinkConfig,
} from '../types/nav';
import { PersonTypes } from './person';

const basename = process.env.EBIS_BASE_PATH.replace(/\/$/, '').replace(
  /^\/?/,
  '/'
);

const linkConfigsArr: Array<NavLinkConfig | ProfileNavLinkConfig> = [
  {
    id: 'CLIPBOARD',
    label: 'Clipboard',
    to: '/clipboard',
    toLegacy: '/clipboard',
    icon: 'fa-clipboard',
  },
  {
    id: 'ADVANCED_SEARCH',
    label: 'Advanced Search',
    to: '/advanced-search',
    toLegacy: '/advSearch',
    fromLegacy: ({
      searchType,
      searchId,
      fname,
      lname,
    }: {
      searchType: string;
      searchId: string;
      fname: string;
      lname: string;
    }) =>
      '/advanced-search' +
      (searchType ? `?searchType=${searchType}` : '') +
      (fname ? `&fname=${fname}` : '') +
      (lname ? `&lname=${lname}` : '') +
      (searchId ? `&searchId=${searchId}` : ''),
    icon: 'fa-search',
  },
  {
    id: 'E_SIGNATURE',
    label: 'E-Signature',
    to: '/e-signature',
    toLegacy: '/esig',
    icon: 'fa-signature',
  },
  {
    id: 'REPORTS',
    label: 'Reports',
    to: '/reports/reports',
    toLegacy: '/reports/reports',
    icon: 'fa-chart-column',
  },
  {
    id: 'BLANK_FORMS',
    label: 'Blank Forms',
    to: '/reports/blank-forms',
    toLegacy: '/reports/blank-forms',
  },
  {
    id: 'MLB_DOCUMENTS',
    label: 'MLB Documents',
    to: '/reports/mlb-documents',
    toLegacy: '/reports/mlb-documents',
  },
  {
    id: 'HELP',
    label: 'Help',
    to: '/help',
    toLegacy: '/help',
    icon: 'fa-question-circle',
  },
  {
    id: 'RSTR_MJ_MN_ROSTERS',
    label: 'MJ/MN Rosters',
    to: '/rstr/mj/mn/rosters',
    toLegacy: '/professional-player/rosters',
  },
  {
    id: 'RSTR_MJ_SUMMARY',
    label: 'MJ Summary',
    to: '/rstr/mj/summary',
    toLegacy: '/professional-player/mj-summary',
  },
  {
    id: 'RSTR_MN_SUMMARY',
    label: 'MN Summary',
    to: '/rstr/mn/summary',
  },
  {
    id: 'RSTR_INFO_UTILITY',
    label: 'Roster Info Utility',
    to: '/rstr/info-utility',
  },
  {
    id: 'RSTR_ELIGIBLE_FOR_DUGOUT',
    label: 'Eligible For Dugout',
    to: '/rstr/eligible-for-dugout',
    toLegacy: '/professional-player/dugout-eligible',
  },
  {
    id: 'RSTR_SPECIAL_ROSTERS',
    label: 'Special Rosters',
    to: '/rstr/special-rosters',
    toLegacy: (_params, searchParams: URLSearchParams) =>
      '/professional-player/specialRosters' +
      (searchParams.get('proRosterSnapshotTypeId') !== null
        ? `?proRosterSnapshotTypeId=${searchParams.get(
            'proRosterSnapshotTypeId'
          )}`
        : '') +
      (searchParams.get('orgId') !== null
        ? `&orgId=${searchParams.get('orgId')}`
        : ''),
  },
  {
    id: 'RSTR_ALLSTAR_ROSTERS',
    label: 'All-Star Rosters',
    to: '/rstr/all-star-rosters',
  },
  {
    id: 'RSTR_TWO_WAY_PLAYERS',
    label: 'Two Way Players',
    to: '/rstr/two-way-players',
    toLegacy: '/professional-player/two-way-player',
  },
  {
    id: 'RSTR_CLUB_PLAYER_POOLS',
    label: 'Club Player Pools',
    to: '/rstr/club-player-pools',
    toLegacy: '/professional-player/club-player-pools',
  },
  {
    id: 'RSTR_PARTNER_LEAGUE',
    label: 'Partner League',
    to: '/rstr/partner-league',
    toLegacy: '/professional-player/partner-league',
  },
  {
    id: 'RSTR_DETAILS',
    label: 'Roster Details',
    to: '/rstr/roster-details',
  },
  {
    id: 'WAIVERS',
    label: 'Waivers',
    to: '/wv/requests',
    toLegacy: (_params, searchParams: URLSearchParams) =>
      '/waivers/requests' +
      (searchParams.get('requestDt') !== null
        ? `?requestDt=${searchParams.get('requestDt')}`
        : ''),
  },
  {
    id: 'TX_DAILY_TRANS_BULLETIN',
    label: 'Daily Trans Bulletin',
    to: '/tx/daily-trans-bulletin',
    toLegacy: '/transactions/bulletin',
  },
  {
    id: 'TX_ELECT_FREE_AGENCY_XX_B_',
    label: 'Elect Free Agency XX(B)',
    to: '/tx/elect-free-agency-xx-b',
    toLegacy: '/transactions/free-agency',
  },
  {
    id: 'TX_INJURED_INACTIVE_REHAB',
    label: 'Injured/Inactive/Rehab',
    to: '/tx/injured-inactive-rehab',
    toLegacy: '/transactions/injuredlist',
  },
  {
    id: 'TX_IL_RECERTIFICATION',
    label: 'IL Recertification',
    to: '/tx/il-recertification',
    toLegacy: '/transactions/recertification',
  },
  {
    id: 'TX_PIEF_LIST',
    label: 'PIEF List',
    to: '/tx/pief-list',
    toLegacy: '/transactions/pief-list',
  },
  {
    id: 'TX_PIEF_LIST_FORM',
    label: 'Pief List',
    to: '/tx/pief-list-form',
    toLegacy: (_: Params, searchParams: URLSearchParams) => {
      const listId = searchParams.get('listId') ?? '-1';
      const orgId = searchParams.get('orgId');
      const year = searchParams.get('year');

      return (
        `/transactions/pief-list-form/?listId=${listId}` +
        (orgId ? `&orgId=${orgId}` : '') +
        (year ? `&year=${year}` : '')
      );
    },
    fromLegacy: ({
      listId,
      orgId,
      year,
    }: {
      listId?: string;
      orgId?: string;
      year?: string;
    }) => {
      return (
        `/tx/pief-list-form?listId=${listId ?? '-1'}` +
        (orgId ? `&orgId=${orgId}` : '') +
        (year ? `&year=${year}` : '')
      );
    },
  },
  {
    id: 'TX_PLAYER_TRACKING',
    label: 'Player Tracking',
    to: '/tx/player-tracking',
    toLegacy: '/transactions/player-tracking',
  },
  {
    id: 'TX_TAXI_SQUAD',
    label: 'Taxi Squad',
    to: '/tx/taxi-squad',
    toLegacy: '/transactions/taxi-squad',
  },
  {
    id: 'TX_TRANSACTION_PAYMENTS',
    label: 'Transaction Payments',
    to: '/tx/transaction-payments',
    toLegacy: '/transactions/transaction-payments',
  },
  {
    id: 'MJ_ARB_ELIGIBLES',
    label: 'Arb Eligibles',
    to: '/mj/arb-eligibles',
    toLegacy: '/major-contract-dashboard/arbitration',
  },
  {
    id: 'MJ_AWARD_ENTRY',
    label: 'Award Entry',
    to: '/mj/award-entry',
    toLegacy: '/major-contract-dashboard/award-entry',
  },
  {
    id: 'MJ_CENTRAL_TENDER',
    label: 'Central Tender',
    to: '/mj/central-tender',
    toLegacy: '/major-contract-dashboard/central-tender',
  },
  {
    id: 'MJ_CENTRAL_TENDER_UTILITY',
    label: 'Central Tender',
    to: '/mj/central-tender-utility',
    toLegacy: (_params, searchParams: URLSearchParams) =>
      `/major-contract-dashboard/central-tender-utility?orgCode=${searchParams.get(
        'orgCode'
      )}&year=${searchParams.get('year')}`,
  },
  {
    id: 'MJ_DEFERRED_COMPENSATION',
    label: 'Deferred Compensation',
    to: '/mj/deferred-compensation',
    toLegacy: '/major-contract-dashboard/deferred-comp',
  },
  {
    id: 'MJ_PAYROLL_FORECAST',
    label: 'Payroll Forecast',
    to: '/mj/payroll-forecast',
    toLegacy: '/major-contract-dashboard/payroll-forecast',
  },
  {
    id: 'MJ_PAYROLL_REPORTS',
    label: 'Payroll Reports',
    to: '/mj/payroll-reports',
    toLegacy: '/major-contract-dashboard/payroll-report',
  },
  {
    id: 'MJ_PROJECTED_PAYROLL_IMPACT',
    label: 'Projected Payroll Impact',
    to: '/mj/projected-payroll-impact',
    toLegacy: '/major-contract-dashboard/projected-payroll-impact',
  },
  {
    id: 'MJ_PROJECTED_PAYROLL_IMPACT_FORM',
    label: 'Projected Payroll Impact',
    to: '/mj/projected-payroll-impact-form/:projectedPayrollImpactId',
    toLegacy: ({
      projectedPayrollImpactId,
    }: Params<'projectedPayrollImpactId'>) =>
      `/projected-payroll-impact?projectedPayrollImpactId=${projectedPayrollImpactId}`,
    fromLegacy: ({
      projectedPayrollImpactId,
    }: {
      projectedPayrollImpactId: string;
    }) => `/mj/projected-payroll-impact-form/${projectedPayrollImpactId}`,
  },
  {
    id: 'MJ_PROJECTED_TRADE_IMPACT',
    label: 'Projected Trade Impact',
    to: '/mj/projected-trade-impact',
    toLegacy: '/major-contract-dashboard/projected-trade-impact',
  },
  {
    id: 'MJ_AWARD_BONUS_PACKAGES',
    label: 'Award Bonus Packages',
    to: '/mj/award-bonus-packages',
    toLegacy: '/major-contract-dashboard/award-bonus-packages',
  },
  {
    id: 'MJ_PRE_ARBITRATION',
    label: 'Pre-Arbitration',
    to: '/mj/pre-arbitration',
    toLegacy: '/major-contract-dashboard/pre-arbitration',
  },
  {
    id: 'MJ_CBT_PAYORS',
    label: 'CBT Payors',
    to: '/mj/cbt-payors',
    toLegacy: '/major-contract-dashboard/cbt-payors',
  },
  {
    id: 'MN_ADDENDUM_C',
    label: 'Addendum C',
    to: '/mn/addendum-c',
    toLegacy: '/mncs/addendumCs',
  },
  {
    id: 'MN_DRAFT_CONTRACTS',
    label: 'Draft Contracts',
    to: '/mn/draft-contracts',
    toLegacy: '/mncs/draftContracts',
  },
  {
    id: 'MN_INTL_SIGNING_BONUSES',
    label: 'Intl Signing Bonuses',
    to: '/mn/intl-signing-bonuses',
    toLegacy: '/mncs/intlSigningBonuses',
  },
  {
    id: 'MN_MN_PAYROLL',
    label: 'MN Payroll',
    to: '/mn/mn-payroll',
    toLegacy: '/mncs/mn-payroll',
  },
  {
    id: 'MN_PAYMENT_TRACKER',
    label: 'Payment Tracker',
    to: '/mn/payment-tracker',
    toLegacy: '/mncs/paymentTracker',
  },
  {
    id: 'MN_RULE_4_SIGNING_BONUSES',
    label: 'Rule 4 Signing Bonuses',
    to: '/mn/rule-4-signing-bonuses',
    toLegacy: '/mncs/rule4SigningBonuses',
  },
  {
    id: 'MN_VERIFICATION',
    label: 'Verification',
    to: '/mn/verification',
    toLegacy: '/mncs/mncs',
  },
  {
    id: 'MN_ASSIGNMENT',
    label: 'Assign. & XX(B) Tracking',
    to: '/mn/assignment',
  },
  {
    id: 'R4_ADMIN',
    label: 'Admin',
    to: '/r4domama/admin',
    toLegacy: '/r4-domestic-amateur/admin',
  },
  {
    id: 'R4_PLAYERS',
    label: 'Players',
    to: '/r4domama/players',
    toLegacy: '/r4-domestic-amateur/players',
  },
  {
    id: 'R4_NEW_PLAYER',
    label: 'New Player',
    to: '/r4domama/new-player',
    toLegacy: '/r4-domestic-amateur/dom-ama-new',
  },
  {
    id: 'R4_SCHOOLS',
    label: 'Schools',
    to: '/r4domama/schools',
    toLegacy: '/r4-domestic-amateur/schools',
  },
  {
    id: 'R4_SCHOOL',
    label: 'School',
    to: '/r4domama/school/:schoolId',
    toLegacy: ({ schoolId }: Params<'schoolId'>) =>
      `/school/${schoolId}?schoolId=${schoolId}`,
    fromLegacy: ({ schoolId }: { schoolId: string }) =>
      `/r4domama/school/${schoolId}`,
  },
  {
    id: 'R4_PRE_DRAFT',
    label: 'Pre-Draft',
    to: '/r4/live/pre-draft',
    toLegacy: '/rule4/predraft',
  },
  {
    id: 'R4_DRAFT_ORDER',
    label: 'Draft Order',
    to: '/r4/live/draft-order',
    toLegacy: '/rule4/draftorder',
  },
  {
    id: 'R4_DRAFT_LIVE',
    label: 'Rule 4 Draft',
    to: '/r4/live',
  },
  {
    id: 'R4_DRAFT_DAY_BOC',
    label: 'Draft Day',
    to: '/r4/live/draft-day-boc',
    toLegacy: '/rule4/draft-day-boc',
  },
  {
    id: 'R4_DRAFT_DAY_CLUB',
    label: 'Draft Day',
    to: '/r4/live/draft-day',
    toLegacy: '/rule4/draft-day',
  },
  {
    id: 'R4_DRAFT_DISPLAY',
    label: 'Draft Display',
    to: '/r4/live/draft-display',
    toLegacy: '/rule4/draft-display',
  },
  {
    id: 'R4_DRAFT_TEST_PICKER',
    label: 'Draft Test Picker',
    to: '/r4/live/draft-test-picker',
  },
  {
    id: 'R4_POST_DRAFT',
    label: 'Post Draft',
    to: '/r4/live/post-draft',
    toLegacy: '/rule4/postdraft',
  },
  {
    id: 'R4_CONNECTIVITY',
    label: 'Connectivity',
    to: '/r4/live/connectivity',
    toLegacy: '/rule4/connectivity',
  },
  {
    id: 'R4_COMM_SCREEN_EDITOR',
    label: 'Comm Screen Editor',
    to: '/r4/live/screen-editor',
    toLegacy: '/rule4/commissioner-edit',
  },
  {
    id: 'R4_COMM_SCREEN_DISPLAY',
    label: 'Comm Screen Display',
    to: '/r4-commissioner-display',
    toLegacy: '/commissioner-display',
  },
  {
    id: 'R5_PLAYER_DISPLAY',
    label: 'R5 Draft Display',
    to: '/r5/live/player-display',
  },
  {
    id: 'R5_DRAFT_LIVE',
    label: 'Rule 5 Draft',
    to: '/r5/live',
  },
  {
    id: 'R5_DRAFT_ADMIN',
    label: 'Draft Admin',
    to: '/r5/live/draft-admin',
    toLegacy: '/rule5/draftadmin',
  },
  {
    id: 'R5_DRAFT_HISTORY',
    label: 'Draft History',
    to: '/r5/live/draft-history',
    toLegacy: '/rule5/drafthistory',
  },
  {
    id: 'R5_DRAFT_ORDER',
    label: 'Draft Order',
    to: '/r5/live/draft-order',
    toLegacy: '/rule5/draftorder',
  },
  {
    id: 'R5_RESERVE_LISTS',
    label: 'Reserve Lists',
    to: '/r5/live/reserve-lists',
    toLegacy: '/rule5/reservelists',
  },
  {
    id: 'R5_SELECTABLE_PLAYERS',
    label: 'Selectable Players',
    to: '/r5/live/selectable-players',
    toLegacy: '/rule5/selectableplayers',
  },
  {
    id: 'INT_EXTERNAL_REG',
    label: 'External Reg',
    to: '/int/external-reg',
    toLegacy: '/intl-dashboard/ext-reg',
  },
  {
    id: 'INT_L1_REG',
    label: 'L1 Reg',
    to: '/int/l1-reg',
    toLegacy: '/intl-dashboard/l1-reg',
  },
  {
    id: 'INT_L2_REG',
    label: 'L2 Reg',
    to: '/int/l2-reg',
    toLegacy: '/intl-dashboard/l2-reg',
  },
  {
    id: 'INT_L2_INQUIRIES',
    label: 'L2 Inquiries',
    to: '/int/l2-inquiries',
    toLegacy: '/intl-dashboard/l2-inquiries',
  },
  {
    id: 'INT_NEW_PLAYER',
    label: 'New Player',
    to: '/int/new-player',
    toLegacy: '/intl-dashboard/new-player',
  },
  {
    id: 'INT_WBC_CLUB_COMMS',
    label: 'WBC - Club Comms',
    to: '/int/wbc-club-comms',
    toLegacy: '/intl-dashboard/wbc-club-comm',
  },
  {
    id: 'INT_WBC_CLUB_COMMS_FORM',
    label: 'WBC Club Comm Report',
    to: '/int/wbc-club-comms-form/:reportId',
    toLegacy: ({ reportId }: Params<'reportId'>) =>
      `/event-report?reportId=${reportId}`,
    fromLegacy: ({ reportId }: { reportId: string }) =>
      `/int/wbc-club-comms-form/${reportId}`,
  },
  {
    id: 'INT_WLP_CLUB_COMMS',
    label: 'WLP - Club Comms',
    to: '/int/wlp-club-comms',
    toLegacy: '/intl-dashboard/wlp-club-comm',
  },
  {
    id: 'INT_CLUB_COMM_REPORT_SUMMARY',
    label: 'Club Comm Report Summary',
    to: '/int/club-comm-report-summary/:wlpPlayerRptSummaryId',
    toLegacy: ({ wlpPlayerRptSummaryId }: Params<'wlpPlayerRptSummaryId'>) =>
      `/intl-dashboard/club-comm-report-summary?wlpPlayerRptSummaryId=${wlpPlayerRptSummaryId}`,
  },
  {
    id: 'INT_WLP_REQUESTS',
    label: 'WLP - Requests',
    to: '/int/wlp-requests',
    toLegacy: '/intl-dashboard/wlp-requests',
  },
  {
    id: 'INT_WLP_RESERVE_LISTS',
    label: 'WLP - Reserve Lists',
    to: '/int/wlp-reserve-lists',
    toLegacy: '/intl-dashboard/wlp-reserve-lists',
  },
  {
    id: 'INT_WLP_RESERVE_LIST',
    label: 'WLP - Reserve List',
    to: '/int/wlp-reserve-list/:wlpReserveListId',
    toLegacy: (
      { wlpReserveListId }: Params<'wlpReserveListId'>,
      searchParams: URLSearchParams
    ) =>
      `/intl-dashboard/wlp-reserve-lists-form?wlpReserveListId=${wlpReserveListId}&leagueId=${searchParams.get(
        'leagueId'
      )}`,
    fromLegacy: ({
      wlpReserveListId,
      leagueId,
    }: {
      wlpReserveListId: string;
      leagueId: string;
    }) => `/int/wlp-reserve-list/${wlpReserveListId}?leagueId=${leagueId}`,
  },
  {
    id: 'INT_DRUG_TEST_REQUESTS',
    label: 'Drug Test Requests',
    to: '/int/drug-test-requests',
    toLegacy: '/intl-dashboard/drug-test-requests',
  },
  {
    id: 'SCT_CLIPBOARDS',
    label: 'Clipboards',
    to: '/sct/clipboards',
    toLegacy: '/scouting-dashboard/clipboards',
  },
  {
    id: 'SCT_CREATE_NEW',
    label: 'Create New',
    to: '/sct/create-new',
    toLegacy: '/scouting-dashboard/new-scouting',
  },
  {
    id: 'SCT_SCOUTS',
    label: 'Scouts',
    to: '/sct/scouts',
    toLegacy: '/scouting-dashboard/scouts',
  },
  {
    id: 'SCT_TRAINER_AGENTS',
    label: 'Trainer/Agents',
    to: '/sct/trainer-agents',
    toLegacy: '/scouting-dashboard/trainer-agents',
  },
  {
    id: 'SCT_VIDEO_LOG',
    label: 'Video Log',
    to: '/sct/video-log',
    toLegacy: '/scouting-dashboard/video-log-sheet',
  },
  {
    id: 'CEP_TRACKING',
    label: 'CEP Tracking',
    to: '/cep/tracking',
    toLegacy: '/csp-cep/cep-tracking',
  },
  {
    id: 'CEP_PLAYERS',
    label: 'Players',
    to: '/cep/players',
    toLegacy: '/csp-cep/csp-cep-players',
  },
  {
    id: 'CEP_SCHOOLS',
    label: 'Schools',
    to: '/cep/schools',
    toLegacy: '/csp-cep/csp-cep-schools',
  },
  {
    id: 'EMP_ROSTERS',
    label: 'Rosters',
    to: '/emp/rosters',
    toLegacy: '/employees/rosters',
  },
  {
    id: 'EMP_CONTRACTS',
    label: 'Contracts',
    to: '/emp/contracts',
    toLegacy: '/employees/contracts',
  },
  {
    id: 'EMP_TRANSLATORS',
    label: 'Translators',
    to: '/emp/translators',
    toLegacy: '/employees/translators',
  },
  {
    id: 'EMP_DOCTORS',
    label: 'Doctors',
    to: '/emp/doctors',
    toLegacy: '/employees/doctors',
  },
  {
    id: 'EMP_PERMISSION_REQUESTS',
    label: 'Permission Requests',
    to: '/emp/permission-requests',
    toLegacy: '/employees/permission-requests',
  },
  {
    id: 'COMM_EMAIL_BLASTS',
    label: 'Email Blasts',
    to: '/comm/email-blasts',
    toLegacy: '/communication/email-blasts',
  },
  {
    id: 'COMM_EMAIL_BLAST',
    label: 'Email Blast',
    to: '/comm/email-blast/:templateId',
    toLegacy: ({ templateId }: Params<'templateId'>) =>
      `/email-blast/blast?templateId=${templateId}`,
    fromLegacy: ({ templateId }: { templateId: string }) =>
      `/comm/email-blast/${templateId}`,
  },
  {
    id: 'COMM_PLAYER_MEMO',
    label: 'Player Memo',
    to: '/comm/player-memo',
    toLegacy: '/communication/player-memo',
  },
  {
    id: 'COMM_PLAYER_MEMO_FORM',
    label: 'Player Memo',
    to: '/comm/player-memo-form/:templateId',
    toLegacy: ({ templateId }: Params<'templateId'>) =>
      `/player-memo/memo?templateId=${templateId}`,
    fromLegacy: ({ templateId }: { templateId: string }) =>
      `/comm/player-memo-form/${templateId}`,
  },
  {
    id: 'COMM_MANAGE_NOTIFICATIONS',
    label: 'Manage Notifications',
    to: '/comm/manage-notifications',
  },
  {
    id: 'COMM_NOTIFICATION_SUBS',
    label: 'Notification Subscriptions',
    to: '/comm/manage-notification-subscriptions',
  },
  {
    id: 'COMM_CUSTOM_NOTIFICATION',
    label: 'Custom Notifications',
    to: '/comm/custom-notifications',
  },
  {
    id: 'MANAGE_MY_NOTIFICATIONS',
    label: 'My Notification Subscriptions',
    to: '/comm/manage-my-notifications',
  },
  {
    id: 'SYS_BATCHES',
    label: 'Batches',
    to: '/sys/batch-list',
    toLegacy: '/system-admin/batch-list',
  },
  {
    id: 'SYS_BATCH_UTILITY',
    label: 'Batches',
    to: '/sys/batch-list/:batchId',
    toLegacy: ({ batchId }: Params<'batchId'>) =>
      `/batch-utility?batchId=${batchId}`,
    fromLegacy: ({ batchId }: { batchId: string }) =>
      `/sys/batch-list/${batchId}`,
  },
  {
    id: 'SYS_MNFA_PROCESS',
    label: 'Yearly Minor League Free Agent Process',
    to: '/sys/yearly-mn-fa-process',
    toLegacy: '/system-admin/mn-free-agent-batch',
  },
  {
    id: 'SYS_END_OF_SEASON_SERVICE_PROCESS',
    label: 'End of Year Service Process',
    to: '/sys/end-of-year-service-process',
    toLegacy: '/system-admin/end-of-year-service-batch',
  },
  {
    id: 'SYS_CLUB_SYSTEM_DATES',
    label: 'Club System Dates',
    to: '/sys/club-system-dates',
    toLegacy: '/system-admin/club-system-dates',
  },
  {
    id: 'SYS_EBIS_CONSTANTS',
    label: 'Ebis Constants',
    to: '/sys/ebis-constants',
    toLegacy: '/system-admin/ebis-constants',
  },
  {
    id: 'SYS_EBIS_LINK',
    label: 'Ebis Link',
    to: '/sys/ebis-link',
    toLegacy: '/system-admin/rest-client',
  },
  {
    id: 'SYS_EMAILS',
    label: 'Emails',
    to: '/sys/emails',
    toLegacy: '/system-admin/email-utility',
  },
  {
    id: 'SYS_NIGHTLY_BATCH_PROGRESS',
    label: 'Nightly Batch Progress',
    to: '/sys/nightly-batch-progress',
    toLegacy: '/system-admin/nightly-batch-progress',
  },
  {
    id: 'SYS_ROLES_PERMISSIONS',
    label: 'Roles & Permissions',
    to: '/sys/roles-permissions',
    toLegacy: '/system-admin/roles-and-permissions',
  },
  {
    id: 'SYS_SYSTEM_DATES',
    label: 'System Dates',
    to: '/sys/system-dates',
    toLegacy: '/system-admin/system-dates',
  },
  {
    id: 'SYS_SYSTEM_VALUES',
    label: 'System Values',
    to: '/sys/system-values',
    toLegacy: '/system-admin/lrd',
  },
  {
    id: 'SYS_USER_ACCOUNTS_BOC',
    label: 'User Accounts',
    to: '/sys/user-accounts',
    toLegacy: '/system-admin/user-accounts',
  },
  {
    id: 'SYS_USER_ACCOUNTS_CLUB',
    label: 'User Accounts',
    to: '/sys/user-accounts-club',
    toLegacy: '/system-admin/user-accounts-club',
  },
  {
    id: 'SYS_WLP_USERS',
    label: 'WLP Users',
    to: '/sys/wlp-users',
    toLegacy: '/system-admin/wlp-users',
  },
  {
    id: 'SYS_PARTNER_LEAGUE_USERS',
    label: 'Partner League Users',
    to: '/sys/partner-league-users',
    toLegacy: '/system-admin/partner-league-users',
  },
  {
    id: 'UTIL_AFFILIATES',
    label: 'Affiliates',
    to: '/util/affiliates',
    toLegacy: '/utilities/affiliate',
  },
  {
    id: 'AFFILIATE_PROFILE',
    label: 'Profile',
    to: '/affiliate/:clubInfoId',
    toLegacy: ({ clubInfoId }: Params<'clubInfoId'>) =>
      `/club-affiliate?clubInfoId=${clubInfoId}`,
    fromLegacy: ({ clubInfoId }: { clubInfoId: string }) =>
      `/affiliate/${clubInfoId}`,
  },
  {
    id: 'UTIL_AGENTS_AGENCIES',
    label: 'Agents & Agencies',
    to: '/util/agents-agencies',
    toLegacy: '/utilities/agent-agency',
  },
  {
    id: 'AGENT_PROFILE',
    label: 'Agent',
    to: '/agent/:agentId',
    toLegacy: ({ agentId }: Params<'agentId'>) => `/agent?agentId=${agentId}`,
    fromLegacy: ({ agentId }: { agentId: string }) => `/agent/${agentId}`,
  },
  {
    id: 'AGENCY_PROFILE',
    label: 'Agency',
    to: '/agency/:agencyId',
    toLegacy: ({ agencyId }: Params<'agencyId'>) =>
      `/agency?agencyId=${agencyId}`,
    fromLegacy: ({ agencyId }: { agencyId: string }) => `/agency/${agencyId}`,
  },
  {
    id: 'UTIL_CONTRACT_LANGUAGE',
    label: 'Contract Language',
    to: '/util/contract-language',
    toLegacy: '/utilities/contract-language',
  },
  {
    id: 'UTIL_FILE_ATTACHMENTS',
    label: 'File Attachments',
    to: '/util/file-attachments',
    toLegacy: '/utilities/file-attachments',
  },
  {
    id: 'UTIL_MN_INSURANCE',
    label: 'Mn Insurance',
    to: '/util/mn-insurance',
    toLegacy: '/utilities/mn-insurance',
  },
  {
    id: 'UTIL_MN_INSURANCE_FORM',
    label: 'MN Insurance',
    to: '/util/mn-insurance',
    toLegacy: '/utilities/mn-insurance-form/?billId',
  },
  {
    id: 'UTIL_REAFFILIATION',
    label: 'Reaffiliation',
    to: '/util/reaffiliation',
    toLegacy: '/utilities/reaffiliation',
  },
  {
    id: 'UTIL_SOCIAL_MEDIA',
    label: 'Social Media',
    to: '/util/social-media',
    toLegacy: '/utilities/social-media',
  },

  // TODO: Replace these with actual links to history/info/contracts/etc pages and set these as groups instead
  {
    id: 'MJC',
    label: 'Major Contract',
    to: '/mjc/:id',
    toLegacy: ({ id }: Params<'id'>, searchParams: URLSearchParams) => {
      let address = `/major-contract?mjcId=${id}&playerId=${searchParams.get(
        'playerId'
      )}`;
      const requestId = searchParams.get('requestId');
      if (requestId !== null) {
        address += `&requestId=${requestId}`;
      }
      return address;
    },
    fromLegacy: ({ mjcId }: { mjcId: string }) => `/mjc/${mjcId}`,
  },
  {
    id: 'MJC_PAYROLL',
    label: 'Payroll',
    to: '/mjc/:id/payroll',
    toLegacy: ({ id }: Params<'id'>, searchParams: URLSearchParams) =>
      `/major-contract?mjcId=${id}&playerId=${searchParams.get(
        'playerId'
      )}&loadPayroll=true`,
    fromLegacy: ({ mjcId }: { mjcId: string }) => `/mjc/${mjcId}/payroll`,
  },
  {
    id: 'MNC',
    label: 'Minor Contract',
    to: '/mnc/:id',
    toLegacy: ({ id }: Params<'id'>, searchParams: URLSearchParams) => {
      let address = `/minor-contract?mncId=${id}&playerId=${searchParams.get(
        'playerId'
      )}`;
      const requestId = searchParams.get('requestId');
      if (requestId !== null) {
        address += `&requestId=${requestId}`;
      }
      return address;
    },
    fromLegacy: ({ mncId }: { mncId: string }) => `/mnc/${mncId}`,
  },
  {
    id: 'UEC',
    label: 'Employee Contract',
    to: '/uec/:id',
    toLegacy: ({ id }: Params<'id'>, searchParams: URLSearchParams) =>
      `/emp-contract/contract?empcId=${id}&personId=${searchParams.get(
        'personId'
      )}`,
    fromLegacy: ({ empcId }: { empcId: string }) => `/mjc/${empcId}`,
  },
  {
    id: 'PROFILE_DOM_AMA',
    label: 'Info',
    to: '/profile/dom-ama/:personId',
    toLegacy: ({ personId }: Params<'personId'>) =>
      `/dom-ama-player-profile/info?playerId=${personId}`,
    fromLegacy: ({ playerId }: { playerId: string }) =>
      `/profile/dom-ama/${playerId}`,
    displayPersonTab: (person) =>
      person.personTypeId === PersonTypes.DOMESTIC_AMATEUR,
  },
  {
    id: 'PROFILE_DOM_AMA_SCOUTING',
    label: 'Scouting & Draft',
    to: '/profile/dom-ama/:personId/scouting',
    toLegacy: ({ personId }: Params<'personId'>) =>
      `/dom-ama-player-profile/scouting?playerId=${personId}`,
    fromLegacy: ({ playerId }: { playerId: string }) =>
      `/profile/dom-ama/${playerId}/scouting`,
    displayPersonTab: (person) =>
      person.personTypeId === PersonTypes.DOMESTIC_AMATEUR,
  },
  {
    id: 'PROFILE_DOM_AMA_CONTRACTS',
    label: 'Contracts',
    to: '/profile/dom-ama/:personId/contracts',
    toLegacy: ({ personId }: Params<'personId'>) =>
      `/dom-ama-player-profile/contracts?playerId=${personId}`,
    fromLegacy: ({ playerId }: { playerId: string }) =>
      `/profile/dom-ama/${playerId}/contracts`,
    displayPersonTab: (person) =>
      person.personTypeId === PersonTypes.DOMESTIC_AMATEUR &&
      !person.hasInvestigation &&
      person.hasMinorContracts,
  },
  {
    id: 'PROFILE_DOM_AMA_CONTRACTS_AND_INV',
    label: 'Contracts & Inv',
    hoverLabel: 'Contracts & Investigations',
    to: '/profile/dom-ama/:personId/contracts',
    toLegacy: ({ personId }: Params<'personId'>) =>
      `/dom-ama-player-profile/contracts?playerId=${personId}`,
    fromLegacy: ({ playerId }: { playerId: string }) =>
      `/profile/dom-ama/${playerId}/contracts`,
    displayPersonTab: (person) =>
      person.personTypeId === PersonTypes.DOMESTIC_AMATEUR &&
      person.hasInvestigation,
  },
  {
    id: 'PROFILE_DOM_AMA_SUMMARY',
    label: 'Summary Info',
    to: '/profile/dom-ama/:personId/summary-info',
    toLegacy: ({ personId }: Params<'personId'>) =>
      `/dom-ama-player-profile/summary-info?playerId=${personId}`,
    fromLegacy: ({ playerId }: { playerId: string }) =>
      `/profile/dom-ama/${playerId}/summary-info`,
    mobileOnly: true,
    displayPersonTab: (person) =>
      person.personTypeId === PersonTypes.DOMESTIC_AMATEUR,
  },
  {
    id: 'PROFILE_INT_AMA_INFO',
    label: 'Info',
    to: '/profile/int-ama/:personId/info',
    toLegacy: (
      { personId }: Params<'personId'>,
      _searchParams: URLSearchParams,
      state: { [key: string]: unknown }
    ) => {
      const drugTest = state?.drugTest;

      if (drugTest)
        return `/int-player-profile/info?playerId=${personId}&drugTest=${drugTest}`;
      return `/int-player-profile/info?playerId=${personId}`;
    },
    fromLegacy: ({ playerId }: { playerId: string }) =>
      `/profile/int-ama/${playerId}/info`,
    displayPersonTab: (person) =>
      person.personTypeId === PersonTypes.INTERNATIONAL_AMATEUR ||
      person.personTypeId === PersonTypes.DRUG_TEST,
  },
  {
    id: 'PROFILE_INT_AMA_SUMMARY',
    label: 'Summary Info',
    to: '/profile/int-ama/:personId/summary-info',
    toLegacy: ({ personId }: Params<'personId'>) =>
      `/int-player-profile/summary-info?playerId=${personId}`,
    fromLegacy: ({ playerId }: { playerId: string }) =>
      `/profile/int-ama/${playerId}/summary-info`,
    mobileOnly: true,
    displayPersonTab: (person) =>
      person.personTypeId === PersonTypes.INTERNATIONAL_AMATEUR,
  },
  {
    id: 'PROFILE_INT_AMA_SCOUTING',
    label: 'Scouting',
    to: '/profile/int-ama/:personId/scouting',
    toLegacy: ({ personId }: Params<'personId'>) =>
      `/int-player-profile/scouting?playerId=${personId}`,
    fromLegacy: ({ playerId }: { playerId: string }) =>
      `/profile/int-ama/${playerId}/scouting`,
    displayPersonTab: (person) =>
      person.personTypeId === PersonTypes.INTERNATIONAL_AMATEUR &&
      person.personId !== -1,
  },
  {
    id: 'PROFILE_PRO_HISTORY',
    label: 'Player History',
    to: '/profile/pro/:personId/history',
    toLegacy: ({ personId }: Params<'personId'>) =>
      `/pro-player-profile/history?playerId=${personId}`,
    fromLegacy: ({ playerId }: { playerId: string }) =>
      `/profile/pro/${playerId}/history`,
    displayPersonTab: (person) =>
      person.personTypeId === PersonTypes.PROFESSIONAL &&
      person.personId !== -1,
    alternateEmployeeTab: 'PROFILE_EMP_HISTORY',
  },
  {
    id: 'PROFILE_EMP_HISTORY',
    label: 'Employee History',
    to: '/profile/emp/:personId',
    toLegacy: ({ personId }: Params<'personId'>) =>
      `/pro-player-profile/history?playerId=${personId}`,
    fromLegacy: ({ playerId }: { playerId: string }) =>
      `/profile/emp/${playerId}`,
    displayPersonTab: (person) =>
      person.personTypeId === PersonTypes.EMPLOYEE && person.personId !== -1,
  },
  {
    id: 'PROFILE_PRO_SUMMARY',
    label: 'Summary Info',
    to: '/profile/pro/:personId/summary-info',
    toLegacy: ({ personId }: Params<'personId'>) =>
      `/pro-player-profile/summary-info?playerId=${personId}`,
    fromLegacy: ({ playerId }: { playerId: string }) =>
      `/profile/pro/${playerId}/summary-info`,
    mobileOnly: true,
    displayPersonTab: (person) =>
      person.personTypeId === PersonTypes.PROFESSIONAL &&
      person.personId !== -1,
    alternateEmployeeTab: 'PROFILE_EMP_SUMMARY',
  },
  {
    id: 'PROFILE_PRO_INFO',
    label: 'Info',
    to: '/profile/pro/:personId/info',
    toLegacy: ({ personId }: Params<'personId'>) =>
      `/pro-player-profile/info?playerId=${personId}`,
    fromLegacy: ({ playerId }: { playerId: string }) =>
      `/profile/pro/${playerId}/info`,
    displayPersonTab: (person) =>
      person.personTypeId === PersonTypes.PROFESSIONAL ||
      person.personTypeId === PersonTypes.PARTNER_PORTAL,
    alternateEmployeeTab: 'PROFILE_EMP_INFO',
  },
  {
    id: 'PROFILE_EMP_INFO',
    label: 'Info',
    to: '/profile/emp/:personId/info',
    toLegacy: ({ personId }: Params<'personId'>) =>
      `/pro-player-profile/info?playerId=${personId}&tab=Info&fromReact=true`,
    fromLegacy: ({ playerId }: { playerId: string }) =>
      `/profile/emp/${playerId}/info`,
    displayPersonTab: (person) => person.personTypeId === PersonTypes.EMPLOYEE,
  },
  {
    id: 'PROFILE_EMP_SUMMARY',
    label: 'Summary Info',
    to: '/profile/emp/:personId/summary-info',
    toLegacy: ({ personId }: Params<'personId'>) =>
      `/pro-player-profile/summary-info?playerId=${personId}`,
    fromLegacy: ({ playerId }: { playerId: string }) =>
      `/profile/emp/${playerId}/summary-info`,
    mobileOnly: true,
    displayPersonTab: (person) =>
      person.personTypeId === PersonTypes.EMPLOYEE && person.personId !== -1,
  },
  {
    id: 'PROFILE_PRO_CONTRACTS',
    label: 'Contracts',
    to: '/profile/pro/:personId/contracts',
    toLegacy: ({ personId }: Params<'personId'>) =>
      `/pro-player-profile/contracts?playerId=${personId}`,
    fromLegacy: ({ playerId }: { playerId: string }) =>
      `/profile/pro/${playerId}/contracts`,
    displayPersonTab: (person) =>
      !person.hasInvestigation &&
      person.personTypeId === PersonTypes.PROFESSIONAL &&
      person.personId !== -1,
    alternateEmployeeTab: 'PROFILE_EMP_CONTRACTS',
  },
  {
    id: 'PROFILE_PRO_CONTRACTS_AND_INV',
    label: 'Contracts & Inv',
    hoverLabel: 'Contracts & Investigations',
    to: '/profile/pro/:personId/contracts',
    toLegacy: ({ personId }: Params<'personId'>) =>
      `/pro-player-profile/contracts?playerId=${personId}`,
    fromLegacy: ({ playerId }: { playerId: string }) =>
      `/profile/pro/${playerId}/contracts`,
    displayPersonTab: (person) =>
      person.hasInvestigation &&
      person.personTypeId === PersonTypes.PROFESSIONAL,
    alternateEmployeeTab: 'PROFILE_EMP_CONTRACTS',
  },
  {
    id: 'PROFILE_EMP_CONTRACTS',
    label: 'Contracts',
    to: '/profile/emp/:personId/contracts',
    toLegacy: ({ personId }: Params<'personId'>) =>
      `/pro-player-profile/contracts?playerId=${personId}&tab=Contracts`,
    fromLegacy: ({ playerId }: { playerId: string }) =>
      `/profile/emp/${playerId}/contracts`,
    displayPersonTab: (person) =>
      !person.hasInvestigation &&
      person.pastPro &&
      person.personTypeId === PersonTypes.EMPLOYEE &&
      person.personId !== -1,
  },
  {
    id: 'PROFILE_EMP_CONTRACTS_AND_INV',
    label: 'Contracts',
    to: '/profile/emp/:personId/contracts',
    toLegacy: ({ personId }: Params<'personId'>) =>
      `/pro-player-profile/contracts?playerId=${personId}&tab=Contracts`,
    fromLegacy: ({ playerId }: { playerId: string }) =>
      `/profile/emp/${playerId}/contracts`,
    displayPersonTab: (person) =>
      person.hasInvestigation &&
      person.pastPro &&
      person.personTypeId === PersonTypes.EMPLOYEE &&
      person.personId !== -1,
  },
  {
    id: 'PROFILE_INT_AMA_CONTRACTS',
    label: 'Contracts',
    to: '/profile/int-ama/:personId/contracts',
    toLegacy: ({ personId }: Params<'personId'>) =>
      `/pro-player-profile/contracts?playerId=${personId}`,
    fromLegacy: ({ playerId }: { playerId: string }) =>
      `/profile/int-ama/${playerId}/contracts`,
    displayPersonTab: (person) =>
      person.personTypeId === PersonTypes.INTERNATIONAL_AMATEUR &&
      !person.hasInvestigation &&
      (person.hasMinorContracts || person.hasDrugTestRequests),
  },
  {
    id: 'PROFILE_INT_AMA_CONTRACTS_AND_INV',
    label: 'Contracts & Inv',
    hoverLabel: 'Contracts & Investigations',
    to: '/profile/int-ama/:personId/contracts',
    toLegacy: ({ personId }: Params<'personId'>) =>
      `/pro-player-profile/contracts?playerId=${personId}`,
    fromLegacy: ({ playerId }: { playerId: string }) =>
      `/profile/int-ama/${playerId}/contracts`,
    displayPersonTab: (person) =>
      person.personTypeId === PersonTypes.INTERNATIONAL_AMATEUR &&
      person.hasInvestigation,
  },
  {
    id: 'PROFILE_WV_AND_TX',
    label: 'Waivers & Trans',
    to: '/profile/pro/:personId/transactions',
    toLegacy: ({ personId }: Params<'personId'>) =>
      `/pro-player-profile/transactions?playerId=${personId}`,
    fromLegacy: ({ playerId }: { playerId: string }) =>
      `/profile/pro/${playerId}/transactions`,
    displayPersonTab: (person) =>
      (person.personTypeId === PersonTypes.PROFESSIONAL &&
        person.personId !== -1) ||
      (person.pastPro && person.personTypeId === PersonTypes.EMPLOYEE),
  },
  {
    id: 'PROFILE_PRO_INTERNATIONAL',
    label: 'International',
    to: '/profile/pro/:personId/international',
    toLegacy: ({ personId }: Params<'personId'>) =>
      `/pro-player-profile/international?playerId=${personId}`,
    fromLegacy: ({ playerId }: { playerId: string }) =>
      `/profile/pro/${playerId}/international`,
    displayPersonTab: (person) =>
      (person.hasInternational &&
        person.personTypeId === PersonTypes.PROFESSIONAL) ||
      (person.hasInternational &&
        person.pastPro &&
        person.personTypeId === PersonTypes.EMPLOYEE),
  },
  {
    id: 'PROFILE_DRAFT_AND_SCOUTING',
    label: 'Draft & Scouting',
    to: '/profile/pro/:personId/draft-info',
    toLegacy: ({ personId }: Params<'personId'>) =>
      `/pro-player-profile/draft-info?playerId=${personId}`,
    fromLegacy: ({ playerId }: { playerId: string }) =>
      `/profile/pro/${playerId}/draft-info`,
    displayPersonTab: (person) =>
      (person.personTypeId === PersonTypes.PROFESSIONAL &&
        person.personId !== -1) ||
      (person.pastPro && person.personTypeId === PersonTypes.EMPLOYEE),
  },
  {
    id: 'PROFILE_SERVICE',
    label: 'Service',
    to: '/profile/pro/:personId/service',
    toLegacy: ({ personId }: Params<'personId'>) =>
      `/pro-player-profile/service?playerId=${personId}`,
    fromLegacy: ({ playerId }: { playerId: string }) =>
      `/profile/pro/${playerId}/service`,
    displayPersonTab: (person) =>
      (person.personTypeId === PersonTypes.PROFESSIONAL &&
        person.personId !== -1) ||
      (person.pastPro && person.personTypeId === PersonTypes.EMPLOYEE),
  },
  {
    id: 'PROFILE_STATS',
    label: 'Stats',
    to: '/profile/pro/:personId/stats',
    toLegacy: ({ personId }: Params<'personId'>) =>
      `/pro-player-profile/stats?playerId=${personId}`,
    fromLegacy: ({ playerId }: { playerId: string }) =>
      `/profile/pro/${playerId}/stats`,
    displayPersonTab: (person) =>
      (person.personTypeId === PersonTypes.PROFESSIONAL &&
        person.personId !== -1) ||
      (person.pastPro && person.personTypeId === PersonTypes.EMPLOYEE),
  },
  {
    id: 'EMP_PERMISSION_REQUEST',
    label: 'Employee Permission Request',
    to: '/emp/permissionrequest/:requestId',
    toLegacy: ({ requestId }: Params<'requestId'>) =>
      `/permission-request?requestId=${requestId}`,
    fromLegacy: ({ requestId }: { requestId: string }) =>
      `/emp/permissionrequest/${requestId}`,
  },
  {
    id: 'PROFILE_TRAINER',
    label: 'Trainer/Agent',
    to: '/traineragent/:trainerAgentId',
    toLegacy: (
      { trainerAgentId }: Params<'trainerAgentId'>,
      searchParams: URLSearchParams
    ) => {
      const fromRegistration = searchParams.get('fromRegistration');
      const fromRegistrationValue = fromRegistration
        ? `&fromRegistration=${fromRegistration}`
        : '';
      return `/trainer-agent?trainerAgentId=${trainerAgentId}${fromRegistrationValue}`;
    },
    fromLegacy: ({ trainerAgentId }: { trainerAgentId: string }) =>
      `/traineragent/${trainerAgentId}`,
  },
  {
    id: 'TRANSACTION',
    label: 'Transaction',
    to: '/transaction/:id',
    toLegacy: ({ id }: Params<'id'>, searchParams: URLSearchParams) => {
      const txType = searchParams.get('txType');
      const isMjTx = searchParams.get('isMajorTransaction');
      const playerId = searchParams.get('playerId');
      return (
        `/transaction?txId=${id}` +
        (playerId ? `&playerId=${playerId}` : '') +
        (txType ? `&txType=${txType}` : '') +
        (isMjTx ? `&isMajorTransaction=${isMjTx}` : '')
      );
    },
    fromLegacy: ({
      txId,
      playerId,
      txType,
      isMajorTransaction,
    }: {
      txId: string;
      playerId: string;
      txType: string;
      isMajorTransaction: string;
    }) => {
      const pid = playerId ? `?playerId=${playerId}` : '';
      const txTypeParam = txType ? `&txType=${txType}` : '';
      const isMjTxParam = isMajorTransaction
        ? `&isMajorTransaction=${isMajorTransaction}`
        : '';
      const url = `/transaction/${txId}`;
      return txId !== '-1' ? url : `${url}${pid}${txTypeParam}${isMjTxParam}`;
    },
  },
  {
    id: 'ADDENDUM_C',
    label: 'Addendum C',
    to: '/addendum-c/:id',
    toLegacy: ({ id }: Params<'id'>, searchParams: URLSearchParams) => {
      const blankFormLetter = searchParams.get('blankFormLetter');
      const clubId = searchParams.get('clubId');
      const clubName = searchParams.get('clubName');
      return (
        `/addendum-c?mncCId=${id}&playerId=${searchParams.get('playerId')}` +
        (blankFormLetter ? `&blankFormLetter=${blankFormLetter}` : '') +
        (clubId ? `&clubId=${clubId}` : '') +
        (clubName ? `&clubName=${clubName}` : '')
      );
    },
    fromLegacy: ({ mncCId }: { mncCId: string }) => `/addendum-c/${mncCId}`,
  },
  {
    id: 'ADDENDUM_D',
    label: 'Addendum D',
    to: '/addendum-d/:id',
    toLegacy: ({ id }: Params<'id'>, searchParams: URLSearchParams) =>
      `/addendum-d?mncDId=${id}&playerId=${searchParams.get('playerId')}`,
    fromLegacy: ({ mncDId }: { mncDId: string }) => `/addendum-d/${mncDId}`,
  },
  {
    id: 'CSP_INVOICE',
    label: 'CSP Invoice',
    to: '/csp-invoice/:id',
    toLegacy: ({ id }: Params<'id'>, searchParams: URLSearchParams) =>
      `/csp-invoice?invoiceId=${id}&playerId=${searchParams.get('playerId')}`,
    fromLegacy: ({
      invoiceId,
      playerId,
    }: {
      invoiceId: string;
      playerId: string;
    }) => `/csp-invoice/${invoiceId}?playerId=${playerId}`,
  },
  {
    id: 'CSP_CREDIT',
    label: 'CSP Credit',
    to: '/csp-credit/:id',
    toLegacy: ({ id }: Params<'id'>, searchParams: URLSearchParams) =>
      `/csp-credit?creditId=${id}&playerId=${searchParams.get('playerId')}`,
    fromLegacy: ({ creditId }: { creditId: string }) =>
      `/csp-credit/${creditId}`,
  },
  {
    id: 'CEP_INVOICE',
    label: 'CEP Invoice',
    to: '/cep-invoice/:id',
    toLegacy: ({ id }: Params<'id'>, searchParams: URLSearchParams) =>
      `/cep-invoice?invoiceId=${id}&playerId=${searchParams.get('playerId')}`,
    fromLegacy: ({ invId }: { invId: string }) => `/cep-invoice/${invId}`,
  },
  {
    id: 'CEP_CREDIT',
    label: 'CEP Credit',
    to: '/cep-credit/:id',
    toLegacy: ({ id }: Params<'id'>, searchParams: URLSearchParams) =>
      `/cep-credit?creditId=${id}&playerId=${searchParams.get('playerId')}`,
    fromLegacy: ({ creditId }: { creditId: string }) =>
      `/cep-credit/${creditId}`,
  },
  {
    id: 'CSP',
    label: 'CSP',
    to: '/profile/pro/:personId/csp',
    toLegacy: ({ personId }: Params<'personId'>) =>
      `/pro-player-profile/csp?playerId=${personId}`,
    fromLegacy: ({ playerId }: { playerId: string }) => `/csp/${playerId}`,
    displayPersonTab: (person) =>
      (person.hasCsp && person.personTypeId === PersonTypes.PROFESSIONAL) ||
      (person.hasCsp &&
        person.pastPro &&
        person.personTypeId === PersonTypes.EMPLOYEE),
  },
  {
    id: 'CEP',
    label: 'CEP',
    to: '/profile/pro/:personId/cep',
    toLegacy: ({ personId }: Params<'personId'>) =>
      `/pro-player-profile/cep?playerId=${personId}`,
    fromLegacy: ({ playerId }: { playerId: string }) =>
      `/profile/pro/${playerId}/cep`,
    displayPersonTab: (person) =>
      (person.hasCep && person.personTypeId === PersonTypes.PROFESSIONAL) ||
      (person.hasCep &&
        person.pastPro &&
        person.personTypeId === PersonTypes.EMPLOYEE),
  },
  {
    id: 'CSP_SCHOOL',
    label: 'CSP/CEP School',
    to: '/cspSchool/:schoolId',
    toLegacy: ({ schoolId }: Params<'schoolId'>) =>
      `/cspSchool?schoolId=${schoolId}`,
    fromLegacy: ({ schoolId }: { schoolId: string }) =>
      `/cepSchool/${schoolId}`,
  },
  {
    id: 'SCOUTING_DAILY_REPORT',
    label: 'Daily Report',
    to: '/scouting-report-daily/:reportId',
    toLegacy: ({ reportId }: Params<'reportId'>) =>
      `/scouting/report?reportId=${reportId}&dailyReport=1`,
    fromLegacy: ({ reportId }: { reportId: string }) =>
      `/scouting-report-daily/${reportId}`,
  },
  {
    id: 'SCOUTING_REPORT',
    label: 'Scouting Report',
    to: '/scouting-report/:reportId',
    toLegacy: (
      { reportId }: Params<'reportId'>,
      searchParams: URLSearchParams
    ) =>
      `/scouting/report?reportId=${reportId}&playerId=${searchParams.get(
        'playerId'
      )}`,
    fromLegacy: ({ reportId }: { reportId: string }) =>
      `/scouting-report/${reportId}`,
  },
  {
    id: 'SCOUT',
    label: 'Scout Profile',
    to: '/scout-profile/info/:scoutId',
    toLegacy: ({ scoutId }: Params<'scoutId'>) =>
      `/scout-profile/info?scoutId=${scoutId}`,
    fromLegacy: ({ scoutId }: { scoutId: string }) =>
      `/scout-profile/info/${scoutId}`,
  },
  {
    id: 'SCOUT_PLAYERS',
    label: 'Scout Players',
    to: '/scout-profile/players/:scoutId',
    toLegacy: ({ scoutId }: Params<'scoutId'>) =>
      `/scout-profile/players?scoutId=${scoutId}`,
    fromLegacy: ({ scoutId }: { scoutId: string }) =>
      `/scout-profile/players/${scoutId}`,
  },
  {
    id: 'INVESTIGATION',
    label: 'Investigation',
    to: '/investigation/:id',
    toLegacy: ({ id }: Params<'id'>, searchParams: URLSearchParams) =>
      `/investigations?investigationId=${id}&playerId=${searchParams.get(
        'playerId'
      )}`,
    fromLegacy: ({ investigationId }: { investigationId: string }) =>
      `/investigation/${investigationId}`,
  },
  {
    id: 'INVESTIGATION_REQUEST',
    label: 'Investigation Request',
    to: '/investigation-request/:id',
    toLegacy: ({ id }: Params<'id'>, searchParams: URLSearchParams) =>
      `/investigations/request?investigationRequestId=${id}&playerId=${searchParams.get(
        'playerId'
      )}`,
    fromLegacy: ({
      investigationRequestId,
    }: {
      investigationRequestId: string;
    }) => `/investigation-request/${investigationRequestId}`,
  },
  {
    id: 'DRUG_TEST_REQUEST',
    label: 'Drug Test Request',
    to: '/drug-test-request/:id',
    toLegacy: ({ id }: Params<'id'>, searchParams: URLSearchParams) => {
      const playerId = searchParams.get('playerId');
      const playerIdParam = playerId ? `&playerId=${playerId}` : '';
      const reqId = id === '-1' ? '' : `drugTestRequestId=${id}`;
      const url = `/drug-test/request?${reqId}`;

      return url + playerIdParam;
    },
    fromLegacy: ({ dtrId }: { dtrId: string }) => `/drug-test-request/${dtrId}`,
  },
  {
    id: 'PLAYER_AGENT_MAPPING',
    label: 'Player Agent Mapping',
    to: '/player-agent-mapping/:rosterId',
    toLegacy: ({ rosterId }: Params<'rosterId'>) =>
      `/player-agent-mapping?rosterId=${rosterId}&editMode=true`,
    fromLegacy: ({ rosterId }: { rosterId: string }) =>
      `/player-agent-mapping?rosterId=${rosterId}&editMode=true`,
  },
  {
    id: 'CONTRACT_LANGUAGE',
    label: 'Contract Language',
    to: '/cl/:id',
    toLegacy: ({ id }: Params<'id'>) =>
      `/contract-language/?langId=${id}
      `,
    fromLegacy: ({ id }: { id: string }) => `/cl/${id}`,
  },
  {
    id: 'CONTRACT_LANGUAGE_STANDARD',
    label: 'Standard Language',
    to: '/cl/standard/:id',
    toLegacy: ({ id }: Params<'id'>) => `/standard-language?langId=${id}`,
    fromLegacy: ({ id }: { id: string }) => `/cl/standard/${id}`,
  },
  {
    id: 'AWARD_BONUS_PACKAGE',
    label: 'Award Bonus Package',
    to: '/abp/:packageId',
    toLegacy: ({ packageId }: Params<'packageId'>) =>
      `/award-bonus-package/package?packageId=${packageId}`,
    fromLegacy: ({ packageId }: { packageId: string }) => `/abp/${packageId}`,
  },
];
const linkConfigs: Record<NavLinkId, NavLinkConfig | ProfileNavLinkConfig> =
  linkConfigsArr.reduce(
    (acc, def) => ({ ...acc, [def.id]: def }),
    {} as Record<NavLinkId, NavLinkConfig | ProfileNavLinkConfig>
  );

const rule4Links: NavLinkId[] = [
  'R4_PRE_DRAFT',
  'R4_DRAFT_ORDER',
  'R4_DRAFT_DAY_BOC',
  'R4_DRAFT_DAY_CLUB',
  'R4_DRAFT_DISPLAY',
  'R4_POST_DRAFT',
  'R4_CONNECTIVITY',
];

const rule4DomAmaLinks: NavLinkId[] = [
  'R4_ADMIN',
  'R4_PLAYERS',
  'R4_NEW_PLAYER',
  'R4_SCHOOLS',
];

const groupConfigsArr: Array<NavGroupConfig> = [
  {
    id: 'ROSTERS',
    label: 'Rosters',
    to: '/rstr',
    links: [
      'RSTR_MJ_MN_ROSTERS',
      'RSTR_MJ_SUMMARY',
      'RSTR_MN_SUMMARY',
      'RSTR_INFO_UTILITY',
      'RSTR_ELIGIBLE_FOR_DUGOUT',
      'RSTR_SPECIAL_ROSTERS',
      'RSTR_ALLSTAR_ROSTERS',
      'RSTR_TWO_WAY_PLAYERS',
      'RSTR_CLUB_PLAYER_POOLS',
      'RSTR_PARTNER_LEAGUE',
      'RSTR_DETAILS',
    ],
    defaultLinks: ['RSTR_MJ_SUMMARY', 'RSTR_MJ_MN_ROSTERS'],
  },
  {
    id: 'REPORTS_DASHBOARD',
    label: 'Reports',
    to: '/reports',
    links: ['REPORTS', 'BLANK_FORMS', 'MLB_DOCUMENTS'],
    defaultLinks: ['REPORTS'],
  },
  {
    id: 'WAIVERS',
    label: 'Waivers',
    to: '/wv',
    links: ['WAIVERS'],
    defaultLinks: [],
  },
  {
    id: 'TRANSACTIONS',
    label: 'Transactions',
    to: '/tx',
    links: [
      'TX_DAILY_TRANS_BULLETIN',
      'TX_ELECT_FREE_AGENCY_XX_B_',
      'TX_INJURED_INACTIVE_REHAB',
      'TX_IL_RECERTIFICATION',
      'TX_PIEF_LIST',
      'TX_PIEF_LIST_FORM',
      'TX_PLAYER_TRACKING',
      'TX_TAXI_SQUAD',
      'TX_TRANSACTION_PAYMENTS',
    ],
    defaultLinks: ['TX_INJURED_INACTIVE_REHAB'],
  },
  {
    id: 'MAJOR_CONTRACTS',
    label: 'Major Contracts',
    to: '/mj',
    links: [
      'MJ_ARB_ELIGIBLES',
      'MJ_AWARD_BONUS_PACKAGES',
      'MJ_AWARD_ENTRY',
      'MJ_CBT_PAYORS',
      'MJ_CENTRAL_TENDER',
      'MJ_CENTRAL_TENDER_UTILITY',
      'MJ_DEFERRED_COMPENSATION',
      'MJ_PAYROLL_FORECAST',
      'MJ_PAYROLL_REPORTS',
      'MJ_PRE_ARBITRATION',
      'MJ_PROJECTED_PAYROLL_IMPACT',
      'MJ_PROJECTED_TRADE_IMPACT',
    ],
    defaultLinks: ['MJ_AWARD_ENTRY'],
  },
  {
    id: 'MINOR_CONTRACTS',
    label: 'Minor Contracts',
    to: '/mn',
    links: [
      'MN_ADDENDUM_C',
      'MN_DRAFT_CONTRACTS',
      'MN_INTL_SIGNING_BONUSES',
      'MN_MN_PAYROLL',
      'MN_PAYMENT_TRACKER',
      'MN_RULE_4_SIGNING_BONUSES',
      'MN_VERIFICATION',
      'MN_ASSIGNMENT',
    ],
    defaultLinks: ['MN_VERIFICATION'],
  },
  {
    id: 'RULE_4_DOM_AMA',
    label: 'Domestic Amateur',
    to: '/r4domama',
    links: rule4DomAmaLinks,
    defaultLinks: [rule4DomAmaLinks[0]],
  },
  {
    id: 'RULE_4',
    label: 'Rule 4',
    to: '/r4',
    links: [...rule4Links],
    defaultLinks: [rule4Links[0]],
  },
  {
    id: 'RULE_4_MAIN',
    label: 'Rule 4',
    to: '/r4',
    links: [...rule4DomAmaLinks, ...rule4Links],
    defaultLinks: [rule4Links[0]],
  },
  {
    id: 'RULE_5',
    label: 'Rule 5',
    to: '/r5',
    links: [
      'R5_DRAFT_ADMIN',
      'R5_DRAFT_HISTORY',
      'R5_PLAYER_DISPLAY',
      'R5_DRAFT_ORDER',
      'R5_RESERVE_LISTS',
      'R5_SELECTABLE_PLAYERS',
    ],
    defaultLinks: ['R5_DRAFT_ORDER'],
  },
  {
    id: 'INTERNATIONAL',
    label: 'International',
    to: '/int',
    links: [
      'INT_DRUG_TEST_REQUESTS',
      'INT_EXTERNAL_REG',
      'INT_L1_REG',
      'INT_L2_REG',
      'INT_L2_INQUIRIES',
      'INT_NEW_PLAYER',
      'INT_WBC_CLUB_COMMS',
      'INT_WLP_CLUB_COMMS',
      'INT_CLUB_COMM_REPORT_SUMMARY',
      'INT_WLP_REQUESTS',
      'INT_WLP_RESERVE_LISTS',
    ],
    defaultLinks: ['INT_L1_REG'],
  },
  {
    id: 'SCOUTING',
    label: 'Scouting',
    to: '/sct',
    links: [
      'SCT_CLIPBOARDS',
      'SCT_CREATE_NEW',
      'SCT_SCOUTS',
      'SCT_TRAINER_AGENTS',
      'SCT_VIDEO_LOG',
    ],
    defaultLinks: [],
  },
  {
    id: 'CSP_CEP',
    label: 'CSP/CEP',
    to: '/cep',
    links: ['CEP_TRACKING', 'CEP_PLAYERS', 'CEP_SCHOOLS'],
    defaultLinks: ['CEP_PLAYERS'],
  },
  {
    id: 'EMPLOYEES',
    label: 'Employees',
    to: '/emp',
    links: [
      'EMP_ROSTERS',
      'EMP_CONTRACTS',
      'EMP_DOCTORS',
      'EMP_PERMISSION_REQUESTS',
      'EMP_TRANSLATORS',
    ],
    defaultLinks: ['EMP_ROSTERS'],
  },
  {
    id: 'PROFILE',
    label: 'Profile',
    to: '/profile',
    links: [
      'PROFILE_PRO_SUMMARY',
      'PROFILE_PRO_HISTORY',
      'PROFILE_EMP_HISTORY',
      'PROFILE_PRO_INFO',
      'PROFILE_EMP_INFO',
      'PROFILE_EMP_SUMMARY',
      'PROFILE_PRO_CONTRACTS',
      'PROFILE_PRO_CONTRACTS_AND_INV',
      'PROFILE_EMP_CONTRACTS',
      'PROFILE_EMP_CONTRACTS_AND_INV',
      'PROFILE_WV_AND_TX',
      'PROFILE_PRO_INTERNATIONAL',
      'PROFILE_DRAFT_AND_SCOUTING',
      'PROFILE_SERVICE',
      'PROFILE_STATS',
      'CEP',
      'CSP',

      'PROFILE_INT_AMA_INFO',
      'PROFILE_INT_AMA_SUMMARY',
      'PROFILE_INT_AMA_SCOUTING',
      'PROFILE_INT_AMA_CONTRACTS',
      'PROFILE_INT_AMA_CONTRACTS_AND_INV',

      'PROFILE_DOM_AMA',
      'PROFILE_DOM_AMA_SCOUTING',
      'PROFILE_DOM_AMA_CONTRACTS',
      'PROFILE_DOM_AMA_CONTRACTS_AND_INV',
      'PROFILE_DOM_AMA_SUMMARY',
    ],
    defaultLinks: ['PROFILE_PRO_HISTORY'],
  },
  {
    id: 'COMMUNICATION',
    label: 'Communication',
    to: '/comm',
    links: [
      'COMM_CUSTOM_NOTIFICATION',
      'COMM_EMAIL_BLASTS',
      'COMM_MANAGE_NOTIFICATIONS',
      'COMM_NOTIFICATION_SUBS',
      'COMM_PLAYER_MEMO',
    ],
    defaultLinks: ['COMM_EMAIL_BLASTS'],
  },
  {
    id: 'SYSTEM',
    label: 'System',
    to: '/sys',
    links: [
      'SYS_BATCHES',
      'SYS_CLUB_SYSTEM_DATES',
      'SYS_EBIS_CONSTANTS',
      'SYS_EBIS_LINK',
      'SYS_EMAILS',
      'SYS_NIGHTLY_BATCH_PROGRESS',
      'SYS_PARTNER_LEAGUE_USERS',
      'SYS_ROLES_PERMISSIONS',
      'SYS_SYSTEM_DATES',
      'SYS_SYSTEM_VALUES',
      'SYS_USER_ACCOUNTS_BOC',
      'SYS_USER_ACCOUNTS_CLUB',
      'SYS_WLP_USERS',
    ],
    defaultLinks: ['SYS_SYSTEM_DATES'],
  },
  {
    id: 'UTILITIES',
    label: 'Utilities',
    to: '/util',
    links: [
      'UTIL_AFFILIATES',
      'UTIL_AGENTS_AGENCIES',
      'UTIL_CONTRACT_LANGUAGE',
      'UTIL_FILE_ATTACHMENTS',
      'UTIL_MN_INSURANCE',
      'UTIL_MN_INSURANCE_FORM',
      'UTIL_REAFFILIATION',
      'UTIL_SOCIAL_MEDIA',
    ],
    defaultLinks: [
      'UTIL_AFFILIATES',
      'UTIL_AGENTS_AGENCIES',
      'UTIL_REAFFILIATION',
      'UTIL_CONTRACT_LANGUAGE',
      'UTIL_FILE_ATTACHMENTS',
      'UTIL_MN_INSURANCE',
      'UTIL_MN_INSURANCE_FORM',
    ],
  },
];
const groupConfigs: Record<NavGroupId, NavGroupConfig> = groupConfigsArr.reduce(
  (acc, def) => ({ ...acc, [def.id]: def }),
  {} as Record<NavGroupId, NavGroupConfig>
);

const topNavLinks: NavLinkId[] = [
  'CLIPBOARD',
  'ADVANCED_SEARCH',
  'E_SIGNATURE',
  'REPORTS',
  'HELP',
];

const slideoutNavGroups: NavGroupId[] = [
  'ROSTERS',
  'WAIVERS',
  'TRANSACTIONS',
  'MAJOR_CONTRACTS',
  'MINOR_CONTRACTS',
  'RULE_4_MAIN',
  'RULE_5',
  'INTERNATIONAL',
  'SCOUTING',
  'CSP_CEP',
  'EMPLOYEES',
  'COMMUNICATION',
  'SYSTEM',
  'UTILITIES',
];

const loginConfig = {
  loginPath: '/login',
  loginCallbackPath: '/login/callback',
};

export const NavConfig = {
  basename,
  linkConfigs,
  groupConfigs,
  topNavLinks,
  slideoutNavGroups,
  loginConfig,
};
